import { Component, inject } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { NumberRange } from '../number-range.model';
import { NumberRangeService } from '../number-range.service';

export interface NumberRangeDialogData {
  numberRange?: NumberRange;
}

function rangeValidator(group: AbstractControl): ValidationErrors | null {
  const { min, max, actual } = group.value;
  if (min === null || max === null || actual === null) {
    return null;
  }
  if (min > max) {
    return { minGreaterThanMax: true };
  }
  if (actual < min || actual > max) {
    return { actualOutOfRange: true };
  }
  return null;
}

@Component({
  selector: 'app-number-range-dialog',
  templateUrl: './number-range-dialog.component.html',
  styleUrls: ['./number-range-dialog.component.scss'],
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatProgressBarModule]
})
export class NumberRangeDialogComponent {

  private fb = inject(FormBuilder);
  private numberRangeService = inject(NumberRangeService);
  private dialogRef = inject<MatDialogRef<NumberRangeDialogComponent, NumberRange>>(MatDialogRef);
  private data = inject<NumberRangeDialogData>(MAT_DIALOG_DATA);

  readonly isEdit = !!this.data?.numberRange;
  saving = false;
  errorMessage = '';

  form = this.fb.group({
    key: ['', [Validators.required, Validators.pattern(/\S/)]],
    min: [0 as number | null, [Validators.required, Validators.pattern(/^-?\d+$/)]],
    max: [null as number | null, [Validators.required, Validators.pattern(/^-?\d+$/)]],
    actual: [0 as number | null, [Validators.required, Validators.pattern(/^-?\d+$/)]]
  }, { validators: rangeValidator });

  constructor() {
    if (this.data?.numberRange) {
      this.form.patchValue(this.data.numberRange);
      this.form.controls.key.disable();
    }
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const numberRange: NumberRange = {
      key: value.key!.trim(),
      min: Number(value.min),
      max: Number(value.max),
      actual: Number(value.actual)
    };

    this.saving = true;
    this.errorMessage = '';
    const request$ = this.isEdit
      ? this.numberRangeService.update(numberRange)
      : this.numberRangeService.create(numberRange);

    request$.subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.errorMessage = typeof err.error === 'string' && err.error ? err.error : 'Saving the number range failed.';
      }
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}
